import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Plug, Zap, Building2, Home, Lightbulb, Siren } from "lucide-react";
import { SERVICES, IMAGES } from "@/data/content";
import { Rise } from "@/components/motion/Reveal";

const ICONS = {
  "ev-charger-installation": Plug,
  "panel-upgrades": Zap,
  "commercial-wiring": Building2,
  "general-electrical": Home,
  lighting: Lightbulb,
  emergency: Siren,
};

// Bento layout classes per service id
const SPANS = {
  "ev-charger-installation": "md:col-span-8",
  "panel-upgrades": "md:col-span-4",
  "commercial-wiring": "md:col-span-4",
  "general-electrical": "md:col-span-4",
  lighting: "md:col-span-4",
  security: "md:col-span-6",
  emergency: "md:col-span-6",
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <Rise>
        <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">
              — What We Do
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold leading-[0.95]">
              Electrical services,
              <br />
              <span className="text-black/35">without the shortcuts.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-base md:text-lg text-black/65 leading-relaxed">
            Every job pulled with proper BC permits and inspected to code. From
            single outlets to full service upgrades — one team, one standard.
          </p>
        </div>
        </Rise>

        <motion.div
          className="grid md:grid-cols-12 gap-4 md:gap-6"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08 } },
          }}
        >
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.id] || Zap;
            const isFeature = s.id === "ev-charger-installation";
            return (
              <motion.div
                key={s.id}
                className={SPANS[s.id]}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
                }}
              >
              <Link
                to={`/services/${s.id}`}
                data-testid={`service-card-${s.id}`}
                className={`group relative bg-white border border-[#E4E4E7] hover:border-black transition-all duration-300 p-6 md:p-8 flex flex-col justify-between overflow-hidden h-full ${
                  isFeature ? "md:min-h-[380px] text-white" : "md:min-h-[260px]"
                }`}
              >
                {isFeature && (
                  <>
                    <img
                      src={IMAGES.ev}
                      alt="Electrician installing a Level 2 EV charger on a Vancouver home"
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-transparent" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <div
                    className={`w-10 h-10 border flex items-center justify-center transition-colors ${
                      isFeature
                        ? "border-white/30 group-hover:border-[#E60000]"
                        : "border-[#E4E4E7] group-hover:border-[#E60000]"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 ${isFeature ? "text-white" : "text-black"}`}
                      strokeWidth={2}
                    />
                  </div>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className="relative mt-8 md:mt-12">
                  <h3
                    className={`font-display text-2xl md:text-3xl tracking-tight font-bold leading-tight ${
                      isFeature ? "text-white" : "text-black"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`mt-3 text-sm md:text-base leading-relaxed max-w-md ${
                      isFeature ? "text-white/80" : "text-black/65"
                    }`}
                  >
                    {s.blurb}
                  </p>
                  <div
                    className={`mt-6 flex items-center justify-between border-t pt-4 ${
                      isFeature ? "border-white/20" : "border-[#E4E4E7]"
                    }`}
                  >
                    <span
                      className={`text-[11px] tracking-[0.2em] uppercase font-bold ${
                        isFeature ? "text-white/85" : "text-black/70"
                      }`}
                    >
                      {s.tag}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-bold group-hover:text-[#E60000] transition-colors ${
                        isFeature ? "text-white" : "text-black"
                      }`}
                    >
                      Learn more
                      <ArrowUpRight
                        className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                        strokeWidth={2.5}
                      />
                    </span>
                  </div>
                </div>
              </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
